/**
 * Error Logger - Client-side error logging and user feedback system
 * Captures and logs JavaScript errors, network failures, and user interactions
 */

import { AppError, ErrorType } from '../types/validation';

export interface ErrorLogEntry {
  id: string;
  timestamp: Date;
  error: AppError;
  userAgent: string;
  url: string;
  userId?: string;
  sessionId: string;
  additionalContext?: Record<string, any>;
}

export interface ErrorLoggerConfig {
  enableConsoleLogging: boolean;
  enableRemoteLogging: boolean;
  enableLocalStorage: boolean;
  maxLocalStorageEntries: number;
  remoteEndpoint?: string;
  apiKey?: string;
  enableUserFeedback: boolean;
  enablePerformanceLogging: boolean;
}

export class ErrorLogger {
  private static instance: ErrorLogger;
  private config: ErrorLoggerConfig;
  private sessionId: string;
  private errorQueue: ErrorLogEntry[] = [];
  private isOnline: boolean = navigator.onLine;

  private constructor(config: Partial<ErrorLoggerConfig> = {}) {
    this.config = {
      enableConsoleLogging: true,
      enableRemoteLogging: false,
      enableLocalStorage: true,
      maxLocalStorageEntries: 100,
      enableUserFeedback: true,
      enablePerformanceLogging: true,
      ...config
    };

    this.sessionId = this.generateSessionId();
    this.initializeErrorHandling();
    this.initializeNetworkMonitoring();
  }

  public static getInstance(config?: Partial<ErrorLoggerConfig>): ErrorLogger {
    if (!ErrorLogger.instance) {
      ErrorLogger.instance = new ErrorLogger(config);
    }
    return ErrorLogger.instance;
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private initializeErrorHandling(): void {
    // Global error handler for uncaught JavaScript errors
    window.addEventListener('error', (event) => {
      const error: AppError = {
        type: 'client',
        code: 'JAVASCRIPT_ERROR',
        message: event.message,
        details: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          stack: event.error?.stack
        },
        timestamp: new Date()
      };
      this.logError(error);
    });

    // Global handler for unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      const error: AppError = {
        type: 'client',
        code: 'UNHANDLED_PROMISE_REJECTION',
        message: event.reason?.message || 'Unhandled promise rejection',
        details: {
          reason: event.reason,
          stack: event.reason?.stack
        },
        timestamp: new Date()
      };
      this.logError(error);
    });

    // Resource loading errors
    window.addEventListener('error', (event) => {
      if (event.target && event.target !== window) {
        const target = event.target as HTMLElement;
        const error: AppError = {
          type: 'client',
          code: 'RESOURCE_LOAD_ERROR',
          message: `Failed to load resource: ${target.tagName}`,
          details: {
            tagName: target.tagName,
            src: (target as any).src || (target as any).href,
            outerHTML: target.outerHTML
          },
          timestamp: new Date()
        };
        this.logError(error);
      }
    }, true);
  }

  private initializeNetworkMonitoring(): void {
    // Monitor online/offline status
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.flushErrorQueue();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      const error: AppError = {
        type: 'network',
        code: 'NETWORK_OFFLINE',
        message: 'Network connection lost',
        timestamp: new Date()
      };
      this.logError(error);
    });

    // Monitor performance issues
    if (this.config.enablePerformanceLogging) {
      this.monitorPerformance();
    }
  }

  private monitorPerformance(): void {
    // Monitor long tasks (> 50ms)
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            if (entry.duration > 50) {
              const error: AppError = {
                type: 'client',
                code: 'PERFORMANCE_LONG_TASK',
                message: `Long task detected: ${entry.duration.toFixed(2)}ms`,
                details: {
                  duration: entry.duration,
                  startTime: entry.startTime,
                  name: entry.name
                },
                timestamp: new Date()
              };
              this.logError(error);
            }
          });
        });
        observer.observe({ entryTypes: ['longtask'] });
      } catch (e) {
        // PerformanceObserver not supported or failed to initialize
      }
    }

    // Monitor Core Web Vitals
    this.monitorWebVitals();
  }

  private monitorWebVitals(): void {
    // Monitor Largest Contentful Paint (LCP)
    if ('PerformanceObserver' in window) {
      try {
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          
          if (lastEntry && lastEntry.startTime > 2500) { // LCP > 2.5s is poor
            const error: AppError = {
              type: 'client',
              code: 'PERFORMANCE_POOR_LCP',
              message: `Poor LCP: ${lastEntry.startTime.toFixed(2)}ms`,
              details: {
                lcp: lastEntry.startTime,
                element: (lastEntry as any).element?.tagName
              },
              timestamp: new Date()
            };
            this.logError(error);
          }
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
      } catch (e) {
        // Observer not supported
      }
    }
  }

  public logError(error: AppError, additionalContext?: Record<string, any>): void {
    const logEntry: ErrorLogEntry = {
      id: this.generateErrorId(),
      timestamp: new Date(),
      error,
      userAgent: navigator.userAgent,
      url: window.location.href,
      sessionId: this.sessionId,
      ...(additionalContext && { additionalContext })
    };

    // Console logging
    if (this.config.enableConsoleLogging) {
      this.logToConsole(logEntry);
    }

    // Local storage logging
    if (this.config.enableLocalStorage) {
      this.logToLocalStorage(logEntry);
    }

    // Remote logging
    if (this.config.enableRemoteLogging && this.isOnline) {
      this.logToRemote(logEntry);
    } else if (this.config.enableRemoteLogging) {
      // Queue for later when online
      this.errorQueue.push(logEntry);
    }

    // User feedback
    if (this.config.enableUserFeedback && this.shouldShowUserFeedback(error)) {
      this.showUserFeedback(error);
    }
  }

  private generateErrorId(): string {
    return `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private logToConsole(logEntry: ErrorLogEntry): void {
    const { error } = logEntry;
    const logLevel = this.getLogLevel(error.type);
    
    console[logLevel](`[${error.type.toUpperCase()}] ${error.code}:`, {
      message: error.message,
      details: error.details,
      timestamp: logEntry.timestamp,
      url: logEntry.url,
      sessionId: logEntry.sessionId
    });
  }

  private getLogLevel(errorType: ErrorType): 'error' | 'warn' | 'info' {
    switch (errorType) {
      case 'server':
      case 'client':
        return 'error';
      case 'network':
      case 'validation':
        return 'warn';
      default:
        return 'info';
    }
  }

  private logToLocalStorage(logEntry: ErrorLogEntry): void {
    try {
      const storageKey = 'kiwi_error_logs';
      const existingLogs = JSON.parse(localStorage.getItem(storageKey) || '[]');
      
      // Add new log entry
      existingLogs.push(logEntry);
      
      // Maintain max entries limit
      if (existingLogs.length > this.config.maxLocalStorageEntries) {
        existingLogs.splice(0, existingLogs.length - this.config.maxLocalStorageEntries);
      }
      
      localStorage.setItem(storageKey, JSON.stringify(existingLogs));
    } catch (e) {
      // Local storage might be full or disabled
      console.warn('Failed to log error to localStorage:', e);
    }
  }

  private async logToRemote(logEntry: ErrorLogEntry): Promise<void> {
    if (!this.config.remoteEndpoint) return;

    try {
      const response = await fetch(this.config.remoteEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.config.apiKey && { 'Authorization': `Bearer ${this.config.apiKey}` })
        },
        body: JSON.stringify(logEntry)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (e) {
      // Failed to send to remote, add to queue
      this.errorQueue.push(logEntry);
      console.warn('Failed to send error log to remote endpoint:', e);
    }
  }

  private flushErrorQueue(): void {
    if (this.errorQueue.length === 0) return;

    const queueCopy = [...this.errorQueue];
    this.errorQueue = [];

    queueCopy.forEach(logEntry => {
      this.logToRemote(logEntry);
    });
  }

  private shouldShowUserFeedback(error: AppError): boolean {
    // Don't show feedback for validation errors (handled by forms)
    if (error.type === 'validation') return false;
    
    // Don't show feedback for performance issues
    if (error.code.includes('PERFORMANCE')) return false;
    
    // Show feedback for user-facing errors
    return ['network', 'server', 'not_found'].includes(error.type);
  }

  private showUserFeedback(error: AppError): void {
    // Create user-friendly error message
    const userMessage = this.getUserFriendlyMessage(error);
    
    // Show notification (could integrate with ErrorHandler)
    this.showNotification(userMessage, error.type);
  }

  private getUserFriendlyMessage(error: AppError): string {
    switch (error.code) {
      case 'NETWORK_ERROR':
        return 'Problème de connexion. Vérifiez votre connexion internet.';
      case 'SERVER_ERROR':
        return 'Problème temporaire du serveur. Veuillez réessayer dans quelques instants.';
      case 'NOT_FOUND':
        return 'La ressource demandée n\'a pas été trouvée.';
      case 'RESOURCE_LOAD_ERROR':
        return 'Certains éléments de la page n\'ont pas pu se charger.';
      default:
        return 'Une erreur inattendue s\'est produite.';
    }
  }

  private showNotification(message: string, type: ErrorType): void {
    // Simple notification implementation
    const notification = document.createElement('div');
    notification.className = `error-notification error-notification--${type}`;
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      bottom: 1rem;
      right: 1rem;
      background: var(--error);
      color: white;
      padding: 1rem;
      border-radius: 0.5rem;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      z-index: 10000;
      max-width: 300px;
      animation: slideInUp 0.3s ease-out;
    `;

    document.body.appendChild(notification);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      notification.remove();
    }, 5000);

    // Allow manual dismissal
    notification.addEventListener('click', () => {
      notification.remove();
    });
  }

  // Public methods for manual error logging
  public logNetworkError(message: string, details?: any): void {
    const error: AppError = {
      type: 'network',
      code: 'NETWORK_ERROR',
      message,
      details,
      timestamp: new Date()
    };
    this.logError(error);
  }

  public logValidationError(field: string, message: string): void {
    const error: AppError = {
      type: 'validation',
      code: 'VALIDATION_ERROR',
      message,
      field,
      timestamp: new Date()
    };
    this.logError(error);
  }

  public logUserAction(action: string, details?: any): void {
    const error: AppError = {
      type: 'client',
      code: 'USER_ACTION',
      message: `User action: ${action}`,
      details,
      timestamp: new Date()
    };
    this.logError(error);
  }

  // Get error logs for debugging
  public getErrorLogs(): ErrorLogEntry[] {
    try {
      const storageKey = 'kiwi_error_logs';
      return JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch (e) {
      return [];
    }
  }

  // Clear error logs
  public clearErrorLogs(): void {
    try {
      localStorage.removeItem('kiwi_error_logs');
    } catch (e) {
      console.warn('Failed to clear error logs:', e);
    }
  }
}

// Export singleton instance
export const errorLogger = ErrorLogger.getInstance();